import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { acceptInviteToken, cancelInviteToken, getUserPendingInvites } from "../../api/api";
import { defaultPfpUrl } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  NotificationTabHeader,
  NotificationEmptyState,
  NotificationErrorPanel,
  NotificationRequestCard,
} from "./NotificationTabShared";
import { useUserRealtime } from "../../context/UserRealtimeContext";

/**
 * Pending organization affiliation requests for the current user.
 * @param {Object} props
 * @param {number} props.userId
 * @param {Object} props.user
 * @param {function} [props.refreshUser]
 * @returns {JSX.Element}
 */
function AffiliationsTab({ userId, user, refreshUser }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const { refreshPendingAffiliations } = useUserRealtime();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const email = user?.email || "";
      if (!email) {
        setPendingInvites([]);
        return;
      }
      const next = await getUserPendingInvites(email);
      if (cancelled) return;
      if (next.error || !next.success) {
        setError(next.error || "Failed to load pending invites");
        setPendingInvites([]);
        return;
      }
      setPendingInvites(next.data || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, userId]);

  /**
   * @param {string} inviteToken
   * @param {string} status
   * @returns {Promise<void>}
   */
  const handleRespond = async (inviteToken, status) => {
    setLoading(true);
    setError(null);
    try {
      const result =
        status === "active"
          ? await acceptInviteToken(inviteToken)
          : await cancelInviteToken(inviteToken);

      if (result.success) {
        setPendingInvites((prev) => prev.filter((inv) => inv.token !== inviteToken));
        await refreshPendingAffiliations();
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        setError(result.error || "Failed to respond to invite");
      }
    } catch (err) {
      setError("Network error while responding to invite");
    } finally {
      setLoading(false);
    }
  };

  const header = (
    <NotificationTabHeader
      title="Pending affiliations"
      description="Accept or decline invitations sent to your email."
    />
  );

  if (error) {
    return (
      <div>
        {header}
        <NotificationErrorPanel message={error} />
      </div>
    );
  }

  if (pendingInvites.length === 0) {
    return (
      <div>
        {header}
        <NotificationEmptyState
          title="No pending affiliation requests"
          subtitle="You are all caught up."
        />
      </div>
    );
  }

  return (
    <div>
      {header}
      <ul className="list-none space-y-4 p-0">
        {pendingInvites.map((invite) => {
          const name = invite.organization_name || "Unknown organization";
          const pfp = defaultPfpUrl;
          const orgInitial = name.replace(/\s+/g, " ").trim().slice(0, 2).toUpperCase() || "OR";

          return (
            <li key={invite.token}>
              <NotificationRequestCard>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                    <Avatar className="h-16 w-16 shrink-0 border-2 border-ui-border">
                      <AvatarImage
                        src={pfp}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = defaultPfpUrl;
                        }}
                      />
                      <AvatarFallback className="text-sm text-text-secondary">{orgInitial}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/organization/${invite.org_id}`}
                          className="text-lg font-semibold text-accent transition-colors hover:text-text-accent-hover"
                        >
                          {name}
                        </Link>
                        <Badge variant="secondary" className="font-normal capitalize">
                          pending invite
                        </Badge>
                      </div>
                      {invite.role ? (
                        <p className="text-sm text-text-secondary">
                          Role: <span className="font-medium text-text-primary">{invite.role}</span>
                        </p>
                      ) : null}
                      {invite.email ? (
                        <p className="text-sm leading-relaxed text-text-secondary">Invited email: {invite.email}</p>
                      ) : null}
                      {invite.created_at ? (
                        <p className="text-sm text-text-muted">
                          Requested {new Date(invite.created_at).toLocaleDateString()}
                        </p>
                      ) : null}
                      {invite.expires_at ? (
                        <p className="text-sm text-text-muted">
                          Expires {new Date(invite.expires_at).toLocaleDateString()}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button
                      type="button"
                      onClick={() => handleRespond(invite.token, "active")}
                      disabled={loading}
                    >
                      Accept
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleRespond(invite.token, "rejected")}
                      disabled={loading}
                    >
                      Decline
                    </Button>
                  </div>
                </div>
              </NotificationRequestCard>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default AffiliationsTab;
